/**
 * Metric and Money — the ONLY components that render a figure/money value, so
 * basis, state, coverage and freshness can never be forgotten (UX-004 §4 rule;
 * grammar lint enforces that no number renders outside these).
 */
import type { ReactNode } from "react";
import type { Figure, Money } from "@solarkey/api-client";
import { useLocale, formatNumber, formatMoneyAmount } from "../format";
import { isWithheld, basisLabel } from "../grammar";
import {
  StateWord,
  ProvenanceChip,
  FreshnessMark,
  CoverageBar,
} from "./atoms";

const MONEY_BASIS_LABEL: Record<string, string> = {
  revenue: "Revenue",
  avoided_cost: "Avoided cost",
  deemed: "Deemed",
};

/**
 * Render a Figure. When the figure is withheld (no value, or CANNOT_CHECK /
 * NOT_MEASURED / NOT_APPLICABLE) it shows the state word + reason, never 0.
 * `variantName` is printed beside the value for PR figures (UX-003 §3.9).
 */
export function Metric(props: {
  figure: Figure;
  variantName?: string;
  showBasis?: boolean;
  showProvenance?: boolean;
  showFreshness?: boolean;
  showCoverage?: boolean;
  /** Hide the unit on the value — for a COLUMN whose unit is stated once in the
   *  header (UX-005 §4.9: "each with its unit in the header"). Default shows it. */
  showUnit?: boolean;
  maximumFractionDigits?: number;
  /** Set with `maximumFractionDigits` when the figure sits in a COLUMN:
   *  without it 4 and 4.00 print differently and the decimal points do
   *  not line up. A single figure needs neither. */
  minimumFractionDigits?: number;
  className?: string;
}): ReactNode {
  const locale = useLocale();
  const f = props.figure;

  if (isWithheld(f)) {
    return (
      <span className={`sk-metric${props.className ? " " + props.className : ""}`}>
        <StateWord state={f.state} reason={f.reason} />
        {props.showCoverage && typeof f.coverage_pct === "number" ? (
          <CoverageBar pct={f.coverage_pct} />
        ) : null}
      </span>
    );
  }

  const basis = basisLabel(f.basis);
  const isProvisional = f.state === "PROVISIONAL";

  return (
    <span className={`sk-metric${props.className ? " " + props.className : ""}`}>
      <span className="sk-metric__value">
        {formatNumber(f.value as number, locale, {
          maximumFractionDigits: props.maximumFractionDigits ?? 2,
          ...(props.minimumFractionDigits !== undefined
            ? { minimumFractionDigits: props.minimumFractionDigits }
            : {}),
        })}
      </span>
      {props.showUnit !== false ? (
        <span className="sk-metric__unit">{f.unit}</span>
      ) : null}
      {props.variantName ? (
        <span className="sk-metric__variant">{props.variantName}</span>
      ) : null}
      {props.showBasis !== false && basis ? (
        <span className="sk-chip sk-chip--basis">{basis}</span>
      ) : null}
      {isProvisional ? (
        <span className="sk-metric__provisional" title="Coverage below the floor">
          Provisional
        </span>
      ) : null}
      {props.showCoverage && typeof f.coverage_pct === "number" ? (
        <CoverageBar pct={f.coverage_pct} />
      ) : null}
      {props.showProvenance ? <ProvenanceChip provenance={f.provenance} /> : null}
      {props.showFreshness !== false ? (
        <FreshnessMark freshness={f.freshness} />
      ) : null}
    </span>
  );
}

/**
 * Render a Money object. It is only passed in when the Value lens is present
 * (absent otherwise, UX-005 §2.3), so `money === undefined` renders nothing.
 * No amount / CANNOT_CHECK shows the state word with its reason (e.g. NO_TARIFF).
 */
export function Money(props: {
  money?: Money | null;
  className?: string;
}): ReactNode {
  const locale = useLocale();
  const m = props.money;
  if (!m) return null; // no Value lens → absent, not nulled

  const cls = `sk-money${props.className ? " " + props.className : ""}`;
  if (m.amount === null || m.amount === undefined || m.state === "CANNOT_CHECK") {
    return (
      <span className={cls}>
        <StateWord state={m.state} />
      </span>
    );
  }

  const basis = m.value_basis ? MONEY_BASIS_LABEL[m.value_basis] : null;
  return (
    <span className={cls}>
      <span className="sk-money__amount">
        {formatMoneyAmount(m.amount, m.currency, locale)}
      </span>
      {basis ? <span className="sk-chip sk-chip--basis">{basis}</span> : null}
    </span>
  );
}
